import React from "react";
import { Alert, Descriptions, Space, Statistic, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatEuro } from "../workReports/format";

const percent = bp =>
  `${(Number(bp || 0) / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;

const LEVEL_TYPES = { error: "error", warning: "warning", info: "info" };

/** Server warnings ({ level, message, params }) with amounts in cents formatted. */
export const WarningList = ({ warnings, className }) => {
  const { t, i18n } = useTranslation("dashboard");
  if (!warnings?.length) return null;
  const order = { error: 0, warning: 1, info: 2 };
  const sorted = [...warnings].sort((a, b) => order[a.level] - order[b.level]);
  const params = w => {
    const out = { ...(w.params || {}) };
    for (const key of ["amount", "cap", "used"]) {
      if (typeof out[key] === "number") out[key] = formatEuro(out[key], i18n.language);
    }
    return out;
  };
  return (
    <Space direction="vertical" className={`fu-full ${className || ""}`} size={6}>
      {sorted.map((w, index) => (
        <Alert
          key={`${w.code}-${index}`}
          type={LEVEL_TYPES[w.level] || "info"}
          showIcon
          message={t(w.message, params(w))}
        />
      ))}
    </Space>
  );
};

/** The subsidy (grant) or tax reduction estimate, always labelled as an estimate. */
const EstimateView = ({ estimate, disclaimer, showWarnings = true }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  if (!estimate) return null;
  const tax = estimate.kind === "tax";
  return (
    <div className="fu-estimate">
      <Space wrap size={[24, 12]} className="fu-estimate-figures">
        <Statistic
          title={tax ? t("Estimated tax reduction") : t("Estimated subsidy")}
          value={formatEuro(estimate.subsidyCents, lng)}
        />
        <Statistic title={t("Eligible costs")} value={formatEuro(estimate.eligibleCents, lng)} />
        {!tax && <Statistic title={t("Funding rate")} value={percent(estimate.rateBp)} />}
      </Space>
      <Descriptions size="small" column={{ xs: 1, sm: 2 }} className="mt-2">
        <Descriptions.Item label={t("Cap on eligible costs")}>
          {formatEuro(estimate.capCents, lng)}
        </Descriptions.Item>
        <Descriptions.Item label={t("Counted costs")}>
          {formatEuro(estimate.cappedCents, lng)}
        </Descriptions.Item>
        {!tax && estimate.flatCents > 0 && (
          <Descriptions.Item label={t("Flat bonus")}>
            {formatEuro(estimate.flatCents, lng)}
          </Descriptions.Item>
        )}
        {!tax && estimate.generalRateBp !== estimate.rateBp && (
          <Descriptions.Item label={t("Rate for further units")}>
            {percent(estimate.generalRateBp)}
          </Descriptions.Item>
        )}
      </Descriptions>
      {tax && (
        <Space wrap className="mt-2">
          {estimate.years.map(y => (
            <Tag key={y.offset}>
              {y.year || t("Year {{n}}", { n: y.offset + 1 })}: {formatEuro(y.amountCents, lng)} (
              {y.percent} %)
            </Tag>
          ))}
        </Space>
      )}
      {!tax && estimate.bonuses?.length > 0 && (
        <Space wrap className="mt-2">
          {estimate.bonuses.map(b => (
            <Tag key={b.key} color={b.applied ? "success" : b.selected ? "warning" : "default"}>
              {b.key === "isfp" ? "iSFP" : b.label.split(" (")[0]}{" "}
              {b.flatCents ? formatEuro(b.flatCents, lng) : `${b.percent} %`}
              {b.selected && !b.applied ? ` – ${t("not applicable")}` : ""}
            </Tag>
          ))}
        </Space>
      )}
      <Typography.Paragraph type="secondary" className="fu-small mt-2">
        {t("Estimate only. The approval letter or the tax assessment decides.")}
        {disclaimer ? ` ${disclaimer}` : ""}
      </Typography.Paragraph>
      {showWarnings && <WarningList warnings={estimate.warnings} />}
    </div>
  );
};

export default EstimateView;
